import React, { Component } from 'react';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import AvailableCitiesModal from '../elements/available-cities-modal';

// Premium components
import {
  PremiumHero,
  SectionWrapper,
  FaqAccordion,
  FinalCTA,
  StructuredData,
} from '../elements/premium';
import { startingPriceLabel, getServiceStartingPrice } from '../../utils/pricing';

const COMPARISON_ROWS = [
  { feature: 'Core Focus', rnr: 'Vehicle service & repair (bikes + cars)', uc: 'Home services (salon, cleaning, appliance repair)' },
  { feature: 'Bike Service', rnr: '32+ cities, from \u20B9450', uc: 'Not offered', rnrGood: true, ucWarn: true },
  { feature: 'Car Service', rnr: 'From \u20B92,499, doorstep', uc: 'Not offered', rnrGood: true, ucWarn: true },
  { feature: 'Mechanic Expertise', rnr: 'Certified two-wheeler & four-wheeler mechanics', uc: 'Generalist technicians (AC, plumbing, electrical)', rnrGood: true },
  { feature: 'Service Model', rnr: '100% doorstep, mechanic comes to you', uc: 'Doorstep for home services, not for vehicles' },
  { feature: 'Arrival Time', rnr: '15 minutes avg', uc: 'Scheduled slots (same-day or next-day)' },
  { feature: 'Warranty', rnr: '30 days parts & labour', uc: '30 days (for applicable services)' },
  { feature: 'Cities', rnr: '32+', uc: '50+ (for home services)' },
  { feature: 'Real-time Updates', rnr: 'Photos + WhatsApp', uc: 'In-app tracking', rnrGood: true },
];

const FAQS = [
  {
    q: 'Does Urban Company offer bike or car service?',
    a: 'No. Urban Company focuses on home services such as salon, cleaning, painting, and appliance repair. They do not offer two-wheeler or four-wheeler mechanical service. For doorstep bike and car service, Ride N Repair is the dedicated platform.',
  },
  {
    q: 'Is Urban Company good for vehicle repairs?',
    a: 'Urban Company does not specialise in vehicle service or repair. Their technicians handle home appliances, not motorcycles or cars. Ride N Repair employs certified vehicle mechanics who are trained specifically for two-wheeler and four-wheeler maintenance and repair.',
  },
  {
    q: 'Why choose Ride N Repair over Urban Company for my bike?',
    a: 'Ride N Repair is purpose-built for vehicle service. Our mechanics arrive within 15 minutes, carry OEM-grade parts, and provide real-time photo updates. With service starting at \u20B9450 and a 30-day warranty, you get a specialist experience that a general home-services platform cannot match.',
  },
  {
    q: 'Which platform is better for doorstep service in India?',
    a: 'It depends on the service type. For home services (cleaning, salon, AC repair), Urban Company is well-established. For bike and car service, Ride N Repair is the clear choice with 2,00,000+ customers served across 32+ cities and 100% doorstep delivery.',
  },
  {
    q: 'Can I book car AC repair on Urban Company?',
    a: 'Urban Company offers home AC (split/window) repair, not automotive AC repair. For car AC service and repair at your doorstep, use Ride N Repair\u2014our mechanics carry automotive AC diagnostic equipment and refrigerant.',
  },
];

class UrbanCompanyComparison extends Component {
  render() {
    const { isMobileBrowser, isMobileApp, user, setUser, citiesModal, setCitiesModal } = this.props;

    return (
      <>
        <Helmet>
          <title>Urban Company vs Ride N Repair 2026 — Vehicle Service Comparison</title>
          <meta
            name="description"
            content="Urban Company vs Ride N Repair — does Urban Company offer bike or car service? Side-by-side comparison for doorstep vehicle service in India 2026."
          />
          <meta
            name="keywords"
            content="urban company vs ride n repair, urban company bike service, urban company car service, doorstep bike service, vehicle service app india"
          />
          <link rel="canonical" href="https://www.ridenrepair.com/urban-company-vs-ride-n-repair" />
        </Helmet>

        <StructuredData
          data={{
            '@type': 'WebPage',
            name: 'Urban Company vs Ride N Repair 2026 — Vehicle Service Comparison',
            description: 'Comparison of Urban Company and Ride N Repair for bike and car service in India.',
            url: 'https://www.ridenrepair.com/urban-company-vs-ride-n-repair',
          }}
        />

        {citiesModal && (
          <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />
        )}

        <Header
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          user={user}
          setUser={setUser}
        />

        <PremiumHero
          badge="Comparison 2026"
          title="Urban Company vs Ride N Repair — Which Is Right for Your Vehicle?"
          subtitle="Urban Company excels at home services. For bikes and cars, you need a specialist."
          compact
          isMobileBrowser={isMobileBrowser}
          ctas={[
            { label: 'Book Vehicle Service', href: '/booking', variant: 'primary' },
            { label: 'View Pricing', href: '/service', variant: 'secondary' },
          ]}
        />

        {/* Comparison Table */}
        <SectionWrapper
          id="comparison"
          kicker="Feature Comparison"
          heading="Ride N Repair vs Urban Company — Side by Side"
          subtitle="Different platforms for different needs. Here is how they compare for vehicle service."
        >
          <div className="rr-competitor-table__wrap" style={{ overflowX: 'auto', marginTop: '2rem' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '1rem',
              lineHeight: '1.6',
            }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Feature</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700, color: '#e63946' }}>Ride N Repair</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Urban Company</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                    <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #eee', fontWeight: 600 }}>
                      {row.feature}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #eee' }}>
                      {row.rnrGood && <span style={{ marginRight: '0.4rem' }}>{'\u2705'}</span>}
                      {row.rnr}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', borderBottom: '1px solid #eee' }}>
                      {row.ucWarn && <span style={{ marginRight: '0.4rem' }}>{'\u26A0\uFE0F'}</span>}
                      {row.uc}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionWrapper>

        {/* Why Not Urban Company for Vehicles */}
        <SectionWrapper
          id="vehicle-service"
          variant="gray"
          kicker="Vehicle Service"
          heading="Why Urban Company Is Not Built for Vehicle Service"
          subtitle="Great for your home. Not equipped for your bike or car."
        >
          <div style={{ maxWidth: 800, margin: '0 auto', lineHeight: '1.8' }}>
            <p>
              Urban Company has built an excellent platform for home services\u2014salon, cleaning,
              painting, appliance repair. However, <strong>they do not offer mechanical service for
              bikes or cars</strong>.
            </p>
            <p>
              Vehicle service requires specialised tools, OEM-grade spare parts, diagnostic equipment,
              and mechanics trained specifically in two-wheeler and four-wheeler engines, transmissions,
              brakes, and electrical systems. This is fundamentally different from home appliance repair.
            </p>
            <p>
              <strong>Ride N Repair</strong> was built from day one as a doorstep vehicle service platform.
              Our mechanics are certified, carry all required tools and parts, and arrive within 15 minutes.
              We have served <strong>2,00,000+ customers</strong> across <strong>32+ cities</strong>.
            </p>
          </div>
        </SectionWrapper>

        {/* What Ride N Repair Offers */}
        <SectionWrapper
          id="services"
          kicker="Our Services"
          heading="What You Get with Ride N Repair"
          subtitle="Everything your vehicle needs, delivered to your doorstep."
        >
          <div style={{ maxWidth: 800, margin: '0 auto', lineHeight: '1.8' }}>
            <ul style={{ paddingLeft: '1.5rem' }}>
              <li>General bike service from <strong>{'\u20B9'}450</strong></li>
              <li>Bike repair, oil change, brake pads, battery, tyres</li>
              <li>Car service from <strong>{'\u20B9'}2,499</strong></li>
              <li>Car AC repair, car battery, denting & painting</li>
              <li>Mechanic arrives within <strong>15 minutes</strong></li>
              <li><strong>30-day warranty</strong> on parts and labour</li>
              <li>Real-time photo updates via WhatsApp</li>
              <li>Transparent pricing with no hidden charges</li>
            </ul>
          </div>
        </SectionWrapper>

        {/* FAQ */}
        <SectionWrapper
          id="faq"
          variant="gray"
          kicker="Questions"
          heading="Frequently Asked Questions"
          subtitle="Common questions about Urban Company vs Ride N Repair for vehicle service."
        >
          <FaqAccordion faqs={FAQS} withSchema />
        </SectionWrapper>

        <FinalCTA
          heading="Book Doorstep Vehicle Service Now"
          subtitle={`Bike service from ${startingPriceLabel('bike')} | Car service from ${getServiceStartingPrice('car-service').label} | 32+ cities | 15-minute arrival`}
          ctas={[
            { label: 'Book Now \u2014 From \u20B9450', href: '/booking', variant: 'primary' },
            { label: 'See All Services', href: '/service', variant: 'white-outline' },
          ]}
        />

        <Footer
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          setCitiesModal={setCitiesModal}
        />
      </>
    );
  }
}

export default UrbanCompanyComparison;
